import { useCallback, useEffect, useRef } from "react";
import { RouteParams } from "@/hooks/useRouteParams";
import { ProductListingData } from "@/types/productListing";
import { ListingContext } from "@/utils/setJLData";
import { isAlsoSearched } from "@/utils/alsoSearched";

function buildListingContext(
  index: number,
  route: RouteParams,
  productListingData: ProductListingData,
): ListingContext {
  const { pageType } = route;

  const listing: ListingContext = {
    resultNum: index + 1,
    pageNum: `page ${route.query.page || "1"}`,
    displayNum: productListingData.products?.length || 0,
    totalNumberOfResults: productListingData.results || 0,
    sort:
      (Array.isArray(route.query.sortBy)
        ? route.query.sortBy[0]
        : route.query.sortBy) || "default: relevance",
  };

  if (pageType === "search") {
    const searchTerm = route.query["search-term"] as string;
    const searchMethod = route.query.originalSearchTerm
      ? "search redirect"
      : isAlsoSearched()
        ? "Also searched"
        : "typed search";

    listing.search = {
      term: searchTerm || "",
      typed: searchTerm || "",
      method: searchMethod,
    };
  } else if (
    pageType === "browse" &&
    productListingData.browseInformation?.category?.categoryId
  ) {
    listing.categoryId =
      productListingData.browseInformation.category.categoryId;
  }

  return listing;
}

export function useConstructorListingAnalytics(
  productListingData: ProductListingData,
  route: RouteParams,
) {
  const productListingDataRef = useRef(productListingData);
  const routeRef = useRef(route);

  useEffect(
    function updateProductListingData() {
      productListingDataRef.current = productListingData;
      routeRef.current = route;
    },
    [productListingData, route],
  );

  const handleCardClick = useCallback((event: Event) => {
    const customEvent = event as CustomEvent;
    const { index, pimProductId } = customEvent.detail || {};

    if (typeof index !== "number" || typeof window === "undefined") {
      return;
    }

    const currentRoute = routeRef.current;
    const currentProductListingData = productListingDataRef.current;

    const listing = buildListingContext(
      index,
      currentRoute,
      currentProductListingData,
    );

    try {
      sessionStorage.setItem(
        "plp_jldata_listing",
        JSON.stringify({
          listing,
          productId: pimProductId,
        }),
      );
    } catch {
      // sessionStorage might not be available (private browsing, etc.)
    }
  }, []);

  useEffect(
    function handleListingContext() {
      try {
        sessionStorage.removeItem("plp_jldata_listing");
      } catch {
        // sessionStorage might not be available
      }

      document.body.addEventListener(
        "product-list:card-click",
        handleCardClick,
      );

      return () => {
        document.body.removeEventListener(
          "product-list:card-click",
          handleCardClick,
        );
      };
    },
    [handleCardClick],
  );
}
